import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Spinner from 'components/Spinner';
import { isEmpty } from 'ramda';

import NestItems from './components/NestItems';
import NestBackground from './components/NestBackground';

import { NEST_ITEM_NAMES, NEST_ITEM_TEXT, NEST_ITEM_IMAGES } from './consts';
import { NestContainer, NestSpinner } from './styled';

const buildContributorNestItem = (fetchedNest, user) => {
  const {
    hasCompletedNI,
    hasCompletedRH,
    isCompanyMember,
    isCompanyAdmin,
    isCompanyOwner,
    isIndividualPlanAdmin
  } = user;
  const links = [];

  if (hasCompletedNI) {
    links.push({
      link: '/nomad-insurance/nps?campaign=egg',
      linkTitle: fetchedNest.hasCompletedNPSSurveyNI
        ? 'Nomad Insurance survey completed  ☑️'
        : 'Go to Nomad Insurance survey',
      disabled: fetchedNest.hasCompletedNPSSurveyNI
    });
  }

  if (!isIndividualPlanAdmin && (isCompanyAdmin || isCompanyOwner || isCompanyMember)) {
    links.push({
      link: '/remote-health/nps?campaign=egg',
      linkTitle: fetchedNest.hasCompletedNPSSurveyRH
        ? 'Remote Health survey completed  ☑️'
        : 'Go to Remote Health survey',
      disabled: fetchedNest.hasCompletedNPSSurveyRH
    });
  }

  if (
    hasCompletedRH &&
    (isIndividualPlanAdmin || (!isCompanyMember && !isCompanyOwner && !isCompanyAdmin))
  ) {
    links.push({
      link: '/nomad-health/nps?campaign=egg',
      linkTitle: fetchedNest.hasCompletedNPSSurveyNH
        ? 'Remote Health survey completed  ☑️'
        : 'Go to Remote Health survey',
      disabled: fetchedNest.hasCompletedNPSSurveyNH
    });
  }

  if (links.some((item) => !item.disabled)) {
    // Show popup with survey buttons if any active survey exists
    return {
      name: NEST_ITEM_NAMES.CONTRIBUTOR,
      images: NEST_ITEM_IMAGES.CONTRIBUTOR,
      text: NEST_ITEM_TEXT.CONTRIBUTOR_LEVEL_ZERO,
      links
    };
  }

  return {
    name: NEST_ITEM_NAMES.CONTRIBUTOR,
    images: NEST_ITEM_IMAGES.CONTRIBUTOR,
    text: NEST_ITEM_TEXT.CONTRIBUTOR
  };
};

const buildNestItems = (fetchedNest, user) => {
  return [
    {
      name: NEST_ITEM_NAMES.VETERAN,
      images: NEST_ITEM_IMAGES.VETERAN,
      text: [NEST_ITEM_TEXT.VETERAN]
    },
    {
      name: NEST_ITEM_NAMES.ALTRUIST,
      images: NEST_ITEM_IMAGES.ALTRUIST,
      text: [NEST_ITEM_TEXT.ALTRUIST]
    },
    buildContributorNestItem(fetchedNest, user)
  ];
};

const emptyNestItem = {
  name: NEST_ITEM_NAMES.UNKNOWN,
  images: NEST_ITEM_IMAGES.UNKNOWN,
  level: 0,
  text: [NEST_ITEM_TEXT.UNKNOWN]
};

const Nest = ({ fetchedNest, isLoading, activeProducts }) => {
  const [eggs, setEggs] = useState([]);
  const [nestBgLoaded, setNestBgLoaded] = useState(false);
  const [nestItemsLoaded, setNestItemsLoaded] = useState(false);
  const user = useSelector((state) => state.app.user);

  const isNestLoading = isLoading || !nestBgLoaded || !nestItemsLoaded;

  useEffect(() => {
    if (fetchedNest?.eggs?.length) {
      const nestItems = buildNestItems(fetchedNest, user);
      const mappedEggs = (fetchedNest?.eggs || [])
        .filter((item) => nestItems.find((x) => x.name === item.name))
        .map((item) => ({
          ...item,
          ...nestItems.find((x) => x.name === item.name)
        }));
      const updatedEggs = [...mappedEggs, emptyNestItem];
      setEggs(updatedEggs);
    }
  }, [fetchedNest, user]);

  if (isEmpty(activeProducts)) {
    return null;
  }

  return (
    <NestContainer>
      {isNestLoading && (
        <NestSpinner>
          <Spinner variant={nestBgLoaded ? 'dark' : 'light'} />
        </NestSpinner>
      )}

      {nestBgLoaded && <NestItems eggs={eggs} setNestItemsLoaded={setNestItemsLoaded} />}

      <NestBackground setNestBgLoaded={setNestBgLoaded} />
    </NestContainer>
  );
};

Nest.propTypes = {
  fetchedNest: PropTypes.object,
  isLoading: PropTypes.bool,
  activeProducts: PropTypes.array
};

export default Nest;
